import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { Flex, Text } from "@chakra-ui/react";
import * as Sentry from "@sentry/react";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { Form, FormikProvider, useFormik } from "formik";
import { PlanType } from "sdk/types";
import { routes } from "~/constants";
import { useTrialDiscount } from "~/MemberApp/hooks";
import { useGetMembershipPaymentAction } from "../hooks";
import { BillingAddressCard } from "./BillingAddressCard";
import { PaymentMethodCard } from "./PaymentMethodCard";
import { SelectedPlanCard } from "./SelectedPlanCard";
import { SubmitButton } from "./SubmitButton";
import { Summary } from "./Summary";
import { Values } from "./types";

const { VITE_API_URL } = import.meta.env;

interface Props {
  handleSubmit: () => void;
  type: PlanType;
}

export const Fields: FC<Props> = ({ handleSubmit, type }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [message, setMessage] = useState<string | null>(null);
  const { t } = useTranslation();
  const action = useGetMembershipPaymentAction();
  const trialDiscount = useTrialDiscount();
  // Three-month subscriptions are no longer offered, so the interval is fixed
  // and submitted via a hidden input instead of the billing interval picker.
  const initialBillingInterval = "1-month";

  const hasTrialDiscount = !!trialDiscount?.discount;

  const initialValues: Values = {
    billingInterval: initialBillingInterval,
    promoCode: "",
  };

  const onSubmit = async ({
    promoCode,
    promoCodeId,
    billingInterval,
  }: Values) => {
    if (!stripe || !elements) return;
    setMessage(null);

    if (!!promoCode && !promoCodeId) {
      return setMessage(t("planPayment.promoCode.applyOrClear"));
    }

    const eligibleForPromoCode = billingInterval == "1-month";
    const promotionCodeId = eligibleForPromoCode ? promoCodeId ?? "" : "";

    const url = `${VITE_API_URL}${routes.confirmation}?conceive_plan=${type}&promotion_code_id=${promotionCodeId}&membership_action=${action}&has_trial_discount=${hasTrialDiscount}&billing_interval=${billingInterval}`;

    const { error } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: url,
      },
    });

    // This will only occur when there is an immediate error when confirming
    // the payment.
    // ref: https://stripe.com/docs/billing/subscriptions/build-subscriptions?ui=elements#complete-payment
    if (error) {
      Sentry.captureException(error as any);
    }

    if (error.message) {
      setMessage(error.message);
    } else {
      setMessage("An unexpected error occured.");
    }
  };

  const formik = useFormik({
    initialValues,
    onSubmit,
  });

  const { isSubmitting } = formik;

  const isDisabled = !stripe || !elements || isSubmitting;
  const base = `planPayment.${action}` as const;

  return (
    <FormikProvider value={formik}>
      <Form style={{ width: "100%" }}>
        <Flex align="center" w="full">
          <Flex align="center" direction="column" maxW="1002px" w="full">
            <Flex
              align={{ base: "center", md: "flex-start" }}
              direction={{ base: "column", md: "row" }}
              gap={{ base: "2rem", md: "1rem" }}
              justify={{ base: "flex-start", md: "space-between" }}
              mb={{ base: "2rem" }}
              w="100%"
            >
              <Flex direction="column" gap="1rem" w="full">
                <SelectedPlanCard type={type} />
                <input
                  name="billingInterval"
                  type="hidden"
                  value={initialBillingInterval}
                />
                <PaymentMethodCard />
                <BillingAddressCard />
              </Flex>
              <Summary
                message={message}
                submitButton={
                  <SubmitButton
                    handleSubmit={handleSubmit}
                    isDisabled={isDisabled}
                    isLoading={isSubmitting}
                  />
                }
                type={type}
              />
            </Flex>
            <Text color="th.gray4" variant="small-print">
              {t(`${base}.terms`)}
            </Text>
          </Flex>
        </Flex>
      </Form>
    </FormikProvider>
  );
};
